import LayersService from '@/services/LayersService.js';
import router from '../../../router';

const setActiveLayersOnInit = ({ commit, state, rootState }) => {
  let activeLayersFromURL = JSON.parse(rootState.route.params.activeLayers);

  if (activeLayersFromURL.length != 0) {
    commit('SET_ACTIVE_LAYERS_ON_INIT', activeLayersFromURL);
    let idsToFetch = [];
    activeLayersFromURL.forEach((al) => {
      if (!state.layers[al]) {
        idsToFetch.push(al);
      }
    });
    LayersService.getByIds(idsToFetch).then((response) => {
      commit('SET_LAYERS', response.data);
    });
  } else {
    LayersService.getActiveLayers()
      .then((activeLayers) => {
        commit('SET_ACTIVE_LAYERS_ON_INIT', activeLayers);

        let idsToFetch = [];
        if (activeLayers) {
          activeLayers.forEach((al) => {
            if (!state.layers[al]) {
              idsToFetch.push(al);
            }
          });
          LayersService.getByIds(idsToFetch).then((response) => {
            commit('SET_LAYERS', response.data);
          });
        }
      })
      .catch((err) => {
        console.error('Error: LayersService.getActiveLayers() failed', err);
      });
  }
};

// Fetches all layers
const fetchAllLayers = ({ commit, dispatch }) => {
  return LayersService.getAll()
    .then((response) => {
      commit('SET_LAYERS', response.data);
    })
    .then(() => {
      dispatch('setActiveLayersOnInit');
    })
    .catch((err) => {
      console.error('Error: layers/fetchAllLayers() failed', err);
    });
};

// Fetches all layers from given phase; then it gets IDs of active layers and requests missing ones (from other phases)
const fetchLayersFromPhase = (
  { commit, state, rootState, dispatch },
  [phaseId]
) => {
  if (state.fetchedLayersFromPhase.indexOf(phaseId) <= -1) {
    return LayersService.getByPhase(phaseId)
      .then((response) => {
        commit('SET_LAYERS', response.data);
        commit('LOG_FETCHED_PHASE_LAYERS', phaseId);
      })
      .then(() => {
        dispatch('setActiveLayersOnInit');
      })
      .catch((err) => {
        console.error('Error: layers/fetchGlobalThreads() failed', err);
      });
  }
};

const fetchSubLayers = ({ commit }, layerId) => {
  return LayersService.getSubLayersByParentLayerID(layerId)
    .then((response) => {
      if (Object.keys(response.data).length > 0) {
        commit('SET_SUBLAYERS', response.data);
        commit('SET_LAYERS', response.data);
      }
    })
    .catch((err) => {
      console.error('Error: layers/fetchSubLayers() failed', err);
    });
};

const fetchLayersForIndividualPins = ({ commit }) => {
  return LayersService.getLayersForIndividualPins()
    .then((response) => {
      if (Object.keys(response.data).length > 0) {
        commit('SET_SUBLAYERS', response.data);
        commit('SET_LAYERS', response.data);
      }
    })
    .catch((err) => {
      console.error('Error: layers/fetchLayersForIndividualPins() failed', err);
    });
};

// Adds new Google Maps layer and checks if the current layer should be set as active by default
const addGmLayerAndConditionalInit = (
  { commit, dispatch, state, rootState },
  [gmLayer, layerId]
) => {
  dispatch('addGmLayer', [gmLayer, layerId]).then(() => {
    if (state.activeLayersOnInit) {
      if (state.activeLayersOnInit.indexOf(layerId) > -1) {
        // Is this layer in LocalStorage (should be visible now?)
        let map = rootState.gmaps.map;
        commit('TOGGLE_GM_LAYER', { layerId, map });
        dispatch('toggleLayerInURL', [layerId, true]);
        dispatch('threads/hideMarkersByLayer', layerId, { root: true });
      }
    }

    let activeLayersOpacity = JSON.parse(
      rootState.route.params.activeLayersOpacity
    );

    if (activeLayersOpacity[layerId]) {
      let layer = state.layers[layerId];

      if (layer.type === 'KML') {
        dispatch('changeKMLOpacity', [layerId, activeLayersOpacity[layerId]]);
      } else if (layer.type === 'SVG') {
        dispatch('changeSVGOpacity', [layerId, activeLayersOpacity[layerId]]);
      }
    }
  });
};

// Adds new Google Maps layer [used in addGmlayerAndConditionalinit]
const addGmLayer = ({ commit }, [gmlayer, layerId]) => {
  commit('ADD_GM_LAYER', { gmlayer, layerId });
};

const toggleGmLayer = ({ commit, dispatch, rootState, state }, layerId) => {
  dispatch('toggleLayerInURL', [layerId]);

  let map = rootState.gmaps.map;
  LayersService.toggleActiveLayer(layerId);
  if (state.layers[layerId].isActive) {
    dispatch('disableGmSubLayers', layerId);
  }
  dispatch('threads/hideMarkersByLayer', layerId, { root: true });
  commit('TOGGLE_GM_LAYER', { layerId, map });
};

/**
 * Disables active GM SubLayers
 * @param {*} param0
 * @param { Number } parentId
 */
const disableGmSubLayers = (
  { commit, dispatch, getters, rootState },
  parentId
) => {
  let subLayersFiltered = getters.getActiveSublayersByLayerId(parentId);
  let map = rootState.gmaps.map;
  let subLayers = subLayersFiltered.map((layer) => layer.id);

  subLayers.forEach((layerId) => {
    dispatch('disableLayerInURL', [layerId]);
    LayersService.toggleActiveLayer(layerId);
    commit('DISABLE_GM_LAYER', { layerId, map });
    dispatch('threads/hideMarkersByLayer', layerId, { root: true });
  });
};

const changeSVGOpacity = ({ commit }, [layerId, opacity]) => {
  let opacityFloat = parseFloat(opacity);
  commit('CHANGE_SVG_OPACITY', [layerId, opacityFloat]);
};

const changeKMLOpacity = ({ commit, rootState }, [layerId, opacity]) => {
  let opacityInt = parseInt(opacity);
  let map = rootState.gmaps.map;
  commit('CHANGE_KML_OPACITY', [layerId, opacityInt, map]);
};

const toggleLayerInURL = (
  { rootState, dispatch },
  [layerId, addOnly = false]
) => {
  let activeLayers = JSON.parse(rootState.route.params.activeLayers);
  let index = activeLayers.indexOf(layerId);

  if (index == -1) {
    // Layer is not active => add it to array of activeLayer
    activeLayers.push(layerId);

    let obj = {
      ...rootState.route.params
    };

    obj['activeLayers'] = JSON.stringify(activeLayers);
    // Update URL
    router.replace({ name: 'base', params: obj }).catch(() => {});
  } else if (index > -1 && !addOnly) {
    // Layer is active => disable
    dispatch('disableLayerInURL', layerId);
  }
};

const disableLayerInURL = ({ rootState }, layerId) => {
  let activeLayers = JSON.parse(rootState.route.params.activeLayers);
  let index = activeLayers.indexOf(layerId);

  let obj = {
    ...rootState.route.params
  };

  // Layer is active => remove it from array of activeLayers
  activeLayers.splice(index, 1);

  // --- If layer should be deactived, remove data about opacity ---
  let activeLayersOpacity = JSON.parse(
    rootState.route.params.activeLayersOpacity
  );
  activeLayersOpacity = { ...activeLayersOpacity };

  let indexOpacity = activeLayersOpacity[layerId];

  if (indexOpacity) {
    delete activeLayersOpacity[layerId];
    obj['activeLayersOpacity'] = JSON.stringify(activeLayersOpacity);
  }

  // --- If layer should be deactived, remove data about opened <details> - texts ---
  let openTexts = JSON.parse(rootState.route.params.openTexts);
  let indexOpenTexts = openTexts.indexOf(layerId);
  if (indexOpenTexts) {
    openTexts.splice(index, 1);
    obj['openTexts'] = JSON.stringify(openTexts);
  }
  // --- If layer should be deactived, remove data about opened <details> - layer settings---
  let openSettings = JSON.parse(rootState.route.params.openSettings);
  let indexOpenSettings = openSettings.indexOf(layerId);
  if (indexOpenSettings) {
    openSettings.splice(index, 1);
    obj['openSettings'] = JSON.stringify(openSettings);
  }

  // --- If layer should be deactived, remove data about opened <details> - layer PINS---
  let openPins = JSON.parse(rootState.route.params.openPins);
  let indexOpenPins = openPins.indexOf(layerId);
  if (indexOpenPins) {
    openPins.splice(index, 1);
    obj['openPins'] = JSON.stringify(openPins);
  }

  obj['activeLayers'] = JSON.stringify(activeLayers);
  // Update URL
  router.replace({ name: 'base', params: obj }).catch(() => {});
};

const toggleOpenLayerPinInURL = (
  { rootState },
  [targetId, targetType, openStatus]
) => {
  let obj = {
    ...rootState.route.params
  };

  let openPins = [];
  if (targetType === 'layer') {
    openPins = JSON.parse(obj['openPinLayers']);
  } else {
    openPins = JSON.parse(obj['openPins']);
  }

  let index = openPins.indexOf(targetId);

  if (openStatus !== null) {
    if (index == -1) {
      openPins.push(targetId);
    }
  } else {
    if (index > -1) {
      openPins.splice(index, 1);
    }
  }

  if (targetType === 'layer') {
    obj['openPinLayers'] = JSON.stringify(openPins);
  } else {
    obj['openPins'] = JSON.stringify(openPins);
  }

  router.replace({ name: 'base', params: obj }).catch(() => {});
};

export default {
  setActiveLayersOnInit,
  fetchAllLayers,
  fetchLayersFromPhase,
  fetchSubLayers,
  fetchLayersForIndividualPins,
  addGmLayerAndConditionalInit,
  addGmLayer,
  toggleGmLayer,
  disableGmSubLayers,
  changeSVGOpacity,
  changeKMLOpacity,
  toggleLayerInURL,
  disableLayerInURL,
  toggleOpenLayerPinInURL
};
